import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/**
 * The organizer/wrangler pair runs every Space. The organizer talks to the person in
 * the unified main thread; the wrangler manages the expert fleet behind it. Both are
 * system bots that stay out of the user's bot roster.
 */
export const OrchestratorRoleSchema = z.enum(["organizer", "wrangler", "expert"]);
export type OrchestratorRole = z.infer<typeof OrchestratorRoleSchema>;

/**
 * What the organizer wants done with one user turn. A direct reply or clarification
 * stays in the main thread; every other kind crosses to the wrangler.
 */
export const OrchestratorIntentKindSchema = z.enum([
  "direct_reply",
  "clarify",
  "spawn_goal",
  "amend_goal",
  "cancel_goal",
  "resolve_blocker",
  "status_query",
]);
export type OrchestratorIntentKind = z.infer<typeof OrchestratorIntentKindSchema>;

export const ExpertGoalCheckKindSchema = z.enum([
  "command_exit_0",
  "file_state",
  "grep_match",
  "test_pass",
  "external_fact",
]);
export type ExpertGoalCheckKind = z.infer<typeof ExpertGoalCheckKindSchema>;

/** Where in an expert run's transcript the proof for a Done-when check lives. */
export const ExpertGoalEvidenceSourceSchema = z.enum([
  "command_output",
  "file_read",
  "test_output",
  "tool_output",
]);
export type ExpertGoalEvidenceSource = z.infer<typeof ExpertGoalEvidenceSourceSchema>;

export const EXPERT_GOAL_CHECK_MIN = 2;
export const EXPERT_GOAL_CHECK_MAX = 6;
export const EXPERT_GOAL_OUTCOME_MAX_LENGTH = 400;
export const EXPERT_GOAL_CONTEXT_MAX_LENGTH = 8_000;
export const EXPERT_GOAL_CHECK_STATEMENT_MAX_LENGTH = 400;
export const EXPERT_GOAL_GUARDRAIL_MAX_LENGTH = 300;
export const EXPERT_GOAL_GUARDRAIL_MAX_COUNT = 10;
export const EXPERT_GOAL_TURN_BUDGET_MAX = 200;
export const EXPERT_GOAL_MINUTE_BUDGET_MAX = 1_440;
export const EXPERT_GOAL_EVIDENCE_EXCERPT_MAX_LENGTH = 2_000;

/**
 * The transcript evidence an external_fact check requires: the tool that must be
 * called and the literal text its output must contain. A model's prose summary of a
 * result never satisfies a check on its own.
 */
export const ExpertGoalExternalFactSchema = z.object({
  /** Tool whose recorded output proves the check, such as read_file or run_command. */
  tool: z.string().trim().min(1),
  source: ExpertGoalEvidenceSourceSchema,
  /** Literal text the recorded tool output must contain for the check to pass. */
  mustMatch: z.string().min(1),
  /** Exit status the tool must have reported, when the tool reports one. */
  exitCode: z.number().int().optional(),
});
export type ExpertGoalExternalFact = z.infer<typeof ExpertGoalExternalFactSchema>;

/** The transcript location that proved a check, recorded when the wrangler verifies it. */
export const ExpertGoalCheckEvidenceSchema = z.object({
  /** The expert run whose transcript carried the proof. */
  runId: Id,
  /** The tool call inside that run whose output was read. */
  toolCallId: z.string().min(1),
  tool: z.string().trim().min(1),
  source: ExpertGoalEvidenceSourceSchema,
  /** Sanitized excerpt of the tool output that satisfied the check. */
  excerpt: z.string().max(EXPERT_GOAL_EVIDENCE_EXCERPT_MAX_LENGTH),
  exitCode: z.number().int().nullable().optional(),
  observedAt: IsoDate,
});
export type ExpertGoalCheckEvidence = z.infer<typeof ExpertGoalCheckEvidenceSchema>;

/**
 * One numbered Done-when item. It states a binary fact about the world and the evidence
 * that settles it, so the wrangler can verify it from the transcript instead of trusting
 * the expert's summary.
 */
export const ExpertGoalCheckSchema = z
  .object({
    id: Id,
    /** 1-based position in the numbered Done-when list. */
    index: z.number().int().min(1).max(EXPERT_GOAL_CHECK_MAX),
    /** One statement that is true or false, never a judgement call. */
    statement: z.string().trim().min(1).max(EXPERT_GOAL_CHECK_STATEMENT_MAX_LENGTH),
    kind: ExpertGoalCheckKindSchema,
    /** Shell command whose exit status proves the check. */
    command: z.string().trim().min(1).optional(),
    /** File or directory whose state proves the check. */
    path: z.string().trim().min(1).optional(),
    /** Literal text or regular expression the inspected output must contain. */
    pattern: z.string().min(1).optional(),
    externalFact: ExpertGoalExternalFactSchema.optional(),
    /** True only with transcript evidence; a prose claim never verifies a check. */
    verified: z.boolean().default(false),
    evidence: ExpertGoalCheckEvidenceSchema.optional(),
  })
  .superRefine((check, ctx) => {
    const requireField = (
      field: "command" | "path" | "pattern" | "externalFact",
      message: string,
    ) => {
      if (check[field] === undefined) ctx.addIssue({ code: "custom", message, path: [field] });
    };
    if (check.kind === "command_exit_0" || check.kind === "test_pass") {
      requireField("command", `A ${check.kind} check needs the command that must succeed`);
    }
    if (check.kind === "file_state") {
      requireField("path", "A file_state check needs the path to inspect");
    }
    if (check.kind === "grep_match") {
      requireField("path", "A grep_match check needs the path to search");
      requireField("pattern", "A grep_match check needs the pattern to match");
    }
    if (check.kind === "external_fact") {
      requireField("externalFact", "An external_fact check needs the transcript evidence to read");
    }
    if (check.verified && !check.evidence) {
      ctx.addIssue({
        code: "custom",
        message: "A verified check needs the transcript evidence that proved it",
        path: ["evidence"],
      });
    }
  });
export type ExpertGoalCheck = z.infer<typeof ExpertGoalCheckSchema>;

/** What the wrangler does when the expert cannot finish inside its budget. */
export const ExpertGoalOnBlockSchema = z.enum([
  "ask_user",
  "escalate_to_organizer",
  "stop_and_report",
]);
export type ExpertGoalOnBlock = z.infer<typeof ExpertGoalOnBlockSchema>;

/**
 * The compiled /goal contract handed to an expert. Outcome and Done-when are fixed
 * before the run starts; verification updates only the checks' `verified` and `evidence`.
 */
export const ExpertGoalContractSchema = z
  .object({
    /** One sentence stating the finished result. */
    outcome: z
      .string()
      .trim()
      .min(1)
      .max(EXPERT_GOAL_OUTCOME_MAX_LENGTH)
      .refine((value) => !/[\r\n]/.test(value), "Outcome must be one sentence on one line"),
    /** The numbered binary checklist the wrangler verifies against the transcript. */
    doneWhen: z
      .array(ExpertGoalCheckSchema)
      .min(EXPERT_GOAL_CHECK_MIN, "A /goal contract needs at least two Done-when checks")
      .max(EXPERT_GOAL_CHECK_MAX, "A /goal contract allows at most six Done-when checks"),
    /** Guardrails the expert must not cross while pursuing the outcome. */
    mustNot: z
      .array(z.string().trim().min(1).max(EXPERT_GOAL_GUARDRAIL_MAX_LENGTH))
      .max(EXPERT_GOAL_GUARDRAIL_MAX_COUNT),
    budgetTurns: z.number().int().min(1).max(EXPERT_GOAL_TURN_BUDGET_MAX),
    budgetMinutes: z.number().int().min(1).max(EXPERT_GOAL_MINUTE_BUDGET_MAX),
    onBlock: ExpertGoalOnBlockSchema,
    /** Conversation context the expert needs so it can start without re-reading the thread. */
    context: z.string().trim().max(EXPERT_GOAL_CONTEXT_MAX_LENGTH),
  })
  .superRefine((contract, ctx) => {
    const seenIds = new Set<string>();
    contract.doneWhen.forEach((check, position) => {
      if (check.index !== position + 1) {
        ctx.addIssue({
          code: "custom",
          message: `Done-when check ${check.id} must be numbered ${position + 1}`,
          path: ["doneWhen", position, "index"],
        });
      }
      if (seenIds.has(check.id)) {
        ctx.addIssue({
          code: "custom",
          message: `Duplicate Done-when check id ${check.id}`,
          path: ["doneWhen", position, "id"],
        });
      }
      seenIds.add(check.id);
    });
  });
export type ExpertGoalContract = z.infer<typeof ExpertGoalContractSchema>;

export const ExpertGoalStatusSchema = z.enum([
  "pending",
  "active",
  "verifying",
  "nudge_queued",
  "completed",
  "blocked",
  "cancelled",
]);
export type ExpertGoalStatus = z.infer<typeof ExpertGoalStatusSchema>;

/** Why a goal stopped short and what the organizer needs from the person to unblock it. */
export const ExpertGoalBlockerSchema = z.object({
  reason: z.string().trim().min(1),
  /** Done-when checks still unverified when the goal blocked. */
  checkIds: z.array(Id),
  /** The decision the organizer asks the person for, when it asks one. */
  question: z.string().trim().min(1).optional(),
  /** The person's answer, once the blocker is resolved. */
  resolution: z.string().trim().min(1).optional(),
  createdAt: IsoDate,
  resolvedAt: IsoDate.nullable().optional(),
});
export type ExpertGoalBlocker = z.infer<typeof ExpertGoalBlockerSchema>;

/**
 * One expert goal as the wrangler persists it: the contract, the live verification state
 * of its checklist, and the budget it has spent.
 */
export const ExpertGoalStateSchema = z.object({
  id: Id,
  spaceId: Id,
  /** The unified main thread that carries this goal's expert_goal block. */
  threadId: Id,
  /** The transcript message holding the block the UI updates in place. */
  messageId: Id.optional(),
  expertBotId: Id,
  expertName: z.string(),
  expertDomain: z.string().trim().min(1),
  status: ExpertGoalStatusSchema,
  contract: ExpertGoalContractSchema,
  turnsUsed: z.number().int().nonnegative(),
  nudgeCount: z.number().int().nonnegative(),
  blocker: ExpertGoalBlockerSchema.nullable().optional(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
  completedAt: IsoDate.nullable().optional(),
});
export type ExpertGoalState = z.infer<typeof ExpertGoalStateSchema>;

/** How many Done-when checks the transcript has proven, for the card's N/M count. */
export function verifiedExpertGoalCheckCount(checks: readonly ExpertGoalCheck[]): number {
  return checks.filter((check) => check.verified).length;
}

/** The checks a continuation nudge still has to prove. */
export function pendingExpertGoalChecks(checks: readonly ExpertGoalCheck[]): ExpertGoalCheck[] {
  return checks.filter((check) => !check.verified);
}

/**
 * What the organizer asks the wrangler to do with one user turn. The envelope carries the
 * ids needed to act on the right goal and the words the wrangler compiles into a contract.
 */
export const OrchestratorIntentEnvelopeSchema = z
  .object({
    id: Id,
    spaceId: Id,
    /** The unified main thread the person spoke in. */
    threadId: Id,
    kind: OrchestratorIntentKindSchema,
    fromRole: OrchestratorRoleSchema.default("organizer"),
    toRole: OrchestratorRoleSchema.default("wrangler"),
    /** The user turn this intent was derived from. */
    userMessageId: Id.optional(),
    /** The goal an amend, cancel, resolve, or status intent acts on. */
    goalId: Id.optional(),
    /** Domain label the wrangler reuses or provisions an expert bot for. */
    expertDomain: z.string().trim().min(1).optional(),
    /** What the person asked for, in the organizer's words. */
    request: z.string().trim().min(1).optional(),
    /** A contract the organizer already drafted for the wrangler to confirm. */
    contract: ExpertGoalContractSchema.optional(),
    /** The person's answer to a blocked goal. */
    resolution: z.string().trim().min(1).optional(),
    createdAt: IsoDate,
  })
  .superRefine((intent, ctx) => {
    const requireField = (
      field: "goalId" | "expertDomain" | "request" | "contract" | "resolution",
      message: string,
    ) => {
      if (intent[field] === undefined) ctx.addIssue({ code: "custom", message, path: [field] });
    };
    switch (intent.kind) {
      case "spawn_goal":
        requireField("expertDomain", "A spawn_goal intent needs the expert domain to staff");
        requireField("request", "A spawn_goal intent needs the request to compile");
        break;
      case "amend_goal":
        requireField("goalId", "An amend_goal intent needs the goal it changes");
        requireField("contract", "An amend_goal intent needs the replacement contract");
        break;
      case "cancel_goal":
        requireField("goalId", "A cancel_goal intent needs the goal it cancels");
        break;
      case "resolve_blocker":
        requireField("goalId", "A resolve_blocker intent needs the blocked goal");
        requireField("resolution", "A resolve_blocker intent needs the person's answer");
        break;
      case "status_query":
      case "direct_reply":
      case "clarify":
        break;
    }
  });
export type OrchestratorIntentEnvelope = z.infer<typeof OrchestratorIntentEnvelopeSchema>;

/**
 * The compact goal card persisted in the unified main thread. It carries only what the card
 * renders, so a transcript row stays small while the full contract lives in ExpertGoalState.
 */
export const ExpertGoalBlockSchema = z.object({
  kind: z.literal("expert_goal"),
  goalId: Id,
  expertBotId: Id,
  expertName: z.string(),
  expertDomain: z.string().trim().min(1),
  outcome: z.string(),
  status: ExpertGoalStatusSchema,
  checks: z.array(ExpertGoalCheckSchema),
  turnsUsed: z.number().int().nonnegative(),
  budgetTurns: z.number().int().positive(),
  nudgeCount: z.number().int().nonnegative().optional(),
  blocker: ExpertGoalBlockerSchema.nullable().optional(),
});
export type ExpertGoalBlock = z.infer<typeof ExpertGoalBlockSchema>;

/** The stream event type that carries live goal state to the unified main thread. */
export const ORCHESTRATOR_GOAL_UPDATED_EVENT = "orchestrator.goal.updated" as const;

export const OrchestratorGoalUpdatedReasonSchema = z.enum([
  "spawned",
  "turn_completed",
  "check_verified",
  "nudged",
  "amended",
  "blocked",
  "blocker_resolved",
  "completed",
  "cancelled",
]);
export type OrchestratorGoalUpdatedReason = z.infer<typeof OrchestratorGoalUpdatedReasonSchema>;

export const OrchestratorGoalUpdatedPayloadSchema = z.object({
  goal: ExpertGoalStateSchema,
  reason: OrchestratorGoalUpdatedReasonSchema.optional(),
  /** The transcript message holding the expert_goal block to update in place. */
  messageId: Id.optional(),
});
export type OrchestratorGoalUpdatedPayload = z.infer<typeof OrchestratorGoalUpdatedPayloadSchema>;
